import React, { useState, useEffect, useMemo, useCallback } from "react";
import { fightResultsMap } from "../../constants/FightResultsMap";
import {
  generateResultKey,
  getAvailableRounds,
  getSpecialResults,
  parseResultKey,
} from "../../utils/fightResultsHelper";

const FighterDetails = ({
  fighter,
  moneyline,
  onMoneylineChange,
  onUpdate,
  isLoading = false,
  onUpdateOutcome,
}) => {
  const handleButtonClick = (increment) => {
    const newValue = (moneyline ?? 0) + increment;
    onMoneylineChange({ target: { value: newValue } });
  };

  const fullName = `${fighter.FirstName} ${fighter.LastName}`;
  const record = `${fighter.PreFightWins}-${fighter.PreFightLosses}-${fighter.PreFightDraws}`;

  // --- Outcome state (moved from FightOutcomeForm) ---
  const [selectedResult, setSelectedResult] = useState("");
  const [fightType, setFightType] = useState("");
  const [outcome, setOutcome] = useState("");
  const [round, setRound] = useState("");

  useEffect(() => {
    const initialKey = Object.keys(fightResultsMap).find(
      (key) => fightResultsMap[key].label === fighter?.Outcome,
    );
    if (initialKey) {
      setSelectedResult(initialKey);
      const parsed = parseResultKey(initialKey);
      if (parsed) {
        setFightType(parsed.type);
        setOutcome(parsed.outcome || "");
        setRound(parsed.round || "");
      }
    } else {
      setSelectedResult("");
      setFightType("");
      setOutcome("");
      setRound("");
    }
  }, [fighter?.Outcome]);

  const handleResultUpdate = useCallback(
    (resultKey) => {
      if (!resultKey || !fightResultsMap[resultKey]) return;
      const { label, score } = fightResultsMap[resultKey];
      setSelectedResult(resultKey);
      onUpdateOutcome({
        fighterId: fighter.FighterId,
        outcome: label,
        score,
      });
    },
    [onUpdateOutcome, fighter.FighterId],
  );

  useEffect(() => {
    if (fightType === "special" && selectedResult) {
      handleResultUpdate(selectedResult);
    } else if (fightType && outcome && round) {
      const resultKey = generateResultKey(fightType, outcome, round);
      if (resultKey && fightResultsMap[resultKey]) {
        handleResultUpdate(resultKey);
      }
    }
  }, [fightType, outcome, round, selectedResult, handleResultUpdate]);

  const current = useMemo(() => {
    if (!selectedResult) return null;
    const { label, score } = fightResultsMap[selectedResult] || {};
    return { label, score };
  }, [selectedResult]);

  const handleFightTypeChange = (e) => {
    setFightType(e.target.value);
    setOutcome("");
    setRound("");
    setSelectedResult("");
  };

  const fightTypeId = `fighttype-${fighter.FighterId}`;
  const outcomeId = `outcome-${fighter.FighterId}`;
  const roundId = `round-${fighter.FighterId}`;
  const specialId = `special-${fighter.FighterId}`;

  return (
    <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 space-y-4">
      {/* Header: name + record */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base md:text-lg font-bold text-dark-grey leading-tight">
            {fullName}
          </h3>
          <p className="text-xs text-medium-grey mt-0.5">
            Record: <span className="font-medium text-dark-grey">{record}</span>
          </p>
        </div>
      </div>

      {/* Moneyline editor */}
      <div>
        <label className="block text-xs font-semibold text-medium-grey mb-1">
          Moneyline
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-stretch rounded-md overflow-hidden border border-gray-300">
            <button
              type="button"
              onClick={() => handleButtonClick(-1)}
              className="btn btn--ghost px-3 py-2 rounded-none border-r-0"
              aria-label="Decrease moneyline by 1"
            >
              –
            </button>
            <input
              type="number"
              value={moneyline ?? 0}
              onChange={onMoneylineChange}
              className="input w-24 text-center rounded-none border-0 border-x border-gray-200"
              min="-10000"
              step="1"
              inputMode="numeric"
              aria-label={`Moneyline for ${fullName}`}
            />
            <button
              type="button"
              onClick={() => handleButtonClick(1)}
              className="btn btn--ghost px-3 py-2 rounded-none border-l-0"
              aria-label="Increase moneyline by 1"
            >
              +
            </button>
          </div>
          <button
            type="button"
            onClick={onUpdate}
            disabled={isLoading}
            className={`btn btn--primary px-4 py-2 text-sm ${
              isLoading ? "opacity-60 cursor-not-allowed" : ""
            }`}
            aria-label={`Update moneyline for ${fullName}`}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    className="opacity-25"
                  />
                  <path
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    className="opacity-75"
                  />
                </svg>
                Updating...
              </div>
            ) : (
              "Update"
            )}
          </button>
        </div>
      </div>

      {/* Fight Result */}
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <label className="block text-xs font-semibold text-medium-grey">
            Fight Result
          </label>
          {current && (
            <p className="text-xs text-medium-grey">
              Selected:{" "}
              <span className="font-medium text-dark-grey">
                {current.label}
              </span>
              {typeof current.score === "number" && (
                <span className="ml-1">
                  ({current.score >= 0 ? "+" : ""}
                  {current.score})
                </span>
              )}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Fight Type */}
          <div>
            <label htmlFor={fightTypeId} className="sr-only">
              Fight Type
            </label>
            <select
              id={fightTypeId}
              value={fightType}
              onChange={handleFightTypeChange}
              className="input w-full py-2"
              aria-label={`Select fight type for ${fullName}`}
            >
              <option value="">Select Type</option>
              <option value="mainevent">Main Event</option>
              <option value="standard">Standard</option>
              <option value="special">Other Results</option>
            </select>
          </div>

          {(fightType === "mainevent" || fightType === "standard") && (
            <div>
              <label htmlFor={outcomeId} className="sr-only">
                Outcome
              </label>
              <select
                id={outcomeId}
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                className="input w-full py-2"
                aria-label={`Select outcome for ${fullName}`}
              >
                <option value="">Select Outcome</option>
                <option value="win">Win</option>
                <option value="loss">Loss</option>
              </select>
            </div>
          )}

          {(fightType === "mainevent" || fightType === "standard") &&
            outcome && (
              <div>
                <label htmlFor={roundId} className="sr-only">
                  Round
                </label>
                <select
                  id={roundId}
                  value={round}
                  onChange={(e) => setRound(e.target.value)}
                  className="input w-full py-2"
                  aria-label={`Select round for ${fullName}`}
                >
                  <option value="">Select Round</option>
                  {getAvailableRounds(fightType).map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            )}

          {fightType === "special" && (
            <div>
              <label htmlFor={specialId} className="sr-only">
                Result
              </label>
              <select
                id={specialId}
                value={selectedResult}
                onChange={(e) => setSelectedResult(e.target.value)}
                className="input w-full py-2"
                aria-label={`Select special result for ${fullName}`}
              >
                <option value="">Select Result</option>
                {getSpecialResults().map(({ key, label }) => {
                  const score = fightResultsMap[key]?.score;
                  return (
                    <option key={key} value={key}>
                      {label}
                      {typeof score === "number"
                        ? ` (${score >= 0 ? "+" : ""}${score})`
                        : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FighterDetails;
